import React, { useEffect, useState, useCallback } from "react";
import { Card, Row, Col, Spinner, Table } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useOutletContext } from "react-router-dom";
import Header from "../components/Header";
import { getHost } from "../utils/utils";
import { FiWifi } from "react-icons/fi";

const API_BANDWIDTH = `${getHost()}/api/metrics/bandwidth`;
const getToken = () => localStorage.getItem("jwt");
const RESI_COST_PER_GB = 2.5;

const PROXY_FILTERS = [
  { label: "All", value: "" },
  { label: "Resi", value: "resi" },
  { label: "DC", value: "dc" },
];

function formatBytes(bytes) {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(2)} MB`;
  return `${(bytes / 1e3).toFixed(2)} KB`;
}

function formatCost(bytes) {
  const gb = bytes / 1e9;
  return `$${(gb * RESI_COST_PER_GB).toFixed(2)}`;
}

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  return (
    <div className="bg-dark border border-secondary rounded p-2">
      <p className="text-main fs-12 mb-1">{data.name}</p>
      <p className="text-white-65 fs-11 mb-0">{formatBytes(data.total_bytes_in)}</p>
    </div>
  );
};

const BandwidthCost = () => {
  const isNarrow = window.matchMedia("(max-width: 767px)").matches;
  const { toggleSidebar } = useOutletContext();
  const [bandwidth, setBandwidth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proxyFilter, setProxyFilter] = useState("");

  const fetchBandwidth = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ hours: "24" });
    if (proxyFilter) params.set("proxy_type", proxyFilter);

    try {
      const res = await fetch(`${API_BANDWIDTH}?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBandwidth(data.bandwidth || []);
      }
    } catch {
      // Keep existing state
    } finally {
      setLoading(false);
    }
  }, [proxyFilter]);

  useEffect(() => {
    fetchBandwidth();
  }, [fetchBandwidth]);

  const totalBytes = bandwidth.reduce((sum, b) => sum + (b.total_bytes_in || 0), 0);
  const totalRequests = bandwidth.reduce((sum, b) => sum + (b.total_requests || 0), 0);

  const sortedByBytes = [...bandwidth].sort((a, b) => b.total_bytes_in - a.total_bytes_in);

  const top20Chart = sortedByBytes.slice(0, 20).map((b) => ({
    name: b._id,
    total_bytes_in: b.total_bytes_in || 0,
  }));

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="pb-4 mx-4 content-body">
        <Row className="gx-0 align-items-center">
          <Col md={12}>
            <div className="mb-2">
              <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
                <FiWifi className="text-white" />
              </div>
              <h4 className="fs-22 text-main mb-1">Bandwidth &amp; Cost</h4>
              <p className="text-white-65 fs-14 mb-0">
                Proxy bandwidth consumption and estimated costs (24h)
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <div className="px-4 mb-3">
        <div className="d-flex gap-2">
          {PROXY_FILTERS.map((filter) => (
            <button
              key={filter.value}
              className={`border rounded px-3 py-1 fs-12 fw-500 cursor-pointer ${
                proxyFilter === filter.value
                  ? "bg-white-15 text-main"
                  : "bg-transparent text-white-65 border-dark"
              }`}
              style={{ borderColor: proxyFilter === filter.value ? "#7C98FF" : undefined }}
              onClick={() => setProxyFilter(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 mb-4">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {/* Summary */}
            <Row className="mb-4">
              <Col md={4} className="mb-3">
                <Card className="border border-dark bg-transparent h-100">
                  <Card.Body className="p-3">
                    <p className="text-white-65 fs-12 mb-1">Total Bandwidth</p>
                    <h4 className="fw-600 mb-0" style={{ color: "#7C98FF" }}>
                      {formatBytes(totalBytes)}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-3">
                <Card className="border border-dark bg-transparent h-100">
                  <Card.Body className="p-3">
                    <p className="text-white-65 fs-12 mb-1">
                      Estimated Cost {proxyFilter === "resi" ? "(Resi)" : "(Resi rate)"}
                    </p>
                    <h4 className="fw-600 mb-0" style={{ color: "#FFC107" }}>
                      {formatCost(totalBytes)}
                    </h4>
                    <p className="text-white-65 fs-11 mb-0 mt-1">@ ${RESI_COST_PER_GB}/GB</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-3">
                <Card className="border border-dark bg-transparent h-100">
                  <Card.Body className="p-3">
                    <p className="text-white-65 fs-12 mb-1">Total Requests</p>
                    <h4 className="fw-600 mb-0" style={{ color: "#00E98E" }}>
                      {totalRequests.toLocaleString()}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Top 20 Bar Chart */}
            {top20Chart.length > 0 && (
              <Card className="border border-dark bg-transparent mb-4">
                <Card.Body className="p-3">
                  <p className="text-white-65 fs-12 mb-3">Top 20 Monitors by Bandwidth</p>
                  <ResponsiveContainer width="100%" height={Math.max(400, top20Chart.length * 28)}>
                    <BarChart data={top20Chart} layout="vertical" margin={{ left: isNarrow ? 0 : 120 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" horizontal={false} />
                      <XAxis
                        type="number"
                        stroke="#848484"
                        style={{ fontSize: "10px" }}
                        tick={{ fill: "#848484" }}
                        tickFormatter={(v) => formatBytes(v)}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        stroke="#848484"
                        style={{ fontSize: "10px" }}
                        tick={{ fill: "#848484" }}
                        width={isNarrow ? 90 : 110}
                        tickFormatter={(v) => (isNarrow && v.length > 12 ? v.slice(0, 11) + "…" : v)}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="total_bytes_in" fill="#7C98FF" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            )}

            {/* Full Table */}
            <Card className="border border-dark bg-transparent">
              <Card.Body className="p-3">
                <p className="text-white-65 fs-12 mb-3">All Monitors</p>
                <div className="table-mobile-scroll">
                <Table className="table custom-table table-bordered" hover>
                  <thead>
                    <tr>
                      <th className="fs-13 fw-500 border-bottom">Monitor</th>
                      <th className="fs-13 fw-500 border-bottom">Bandwidth</th>
                      <th className="fs-13 fw-500 border-bottom">Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedByBytes.map((b) => (
                      <tr key={b._id}>
                        <td className="text-main fs-12 py-2">{b._id}</td>
                        <td className="fs-12 py-2" style={{ color: "#7C98FF" }}>
                          {formatBytes(b.total_bytes_in || 0)}
                        </td>
                        <td className="text-white-65 fs-12 py-2">
                          {(b.total_requests || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default BandwidthCost;
